import { Matrix4, Quaternion, Vector3 as ThreeVector3 } from "three";
import { MOON_RADIUS } from "../simulation/constants";
import type { Vector3 } from "../simulation/types";
import { normalize, scale } from "../simulation/vector";

const LOCAL_UP = new ThreeVector3(0, 1, 0);
const BASIS_EPSILON = 1e-12;
const PHASE_MARKER_GAP = 0.34;

function toUnitVector(vector: Vector3, label: string): ThreeVector3 {
  const result = new ThreeVector3(...vector);

  if (
    !Number.isFinite(result.x) ||
    !Number.isFinite(result.y) ||
    !Number.isFinite(result.z) ||
    result.lengthSq() <= BASIS_EPSILON
  ) {
    throw new RangeError(`${label} must be a finite non-zero vector`);
  }

  return result.normalize();
}

export function quaternionFromLocalUp(worldUp: Vector3): Quaternion {
  return new Quaternion().setFromUnitVectors(
    LOCAL_UP,
    toUnitVector(worldUp, "World up"),
  );
}

export function synchronousMoonQuaternion(
  moonToEarth: Vector3,
  orbitalNormal: Vector3,
): Quaternion {
  const localZ = toUnitVector(moonToEarth, "Moon-to-Earth direction");
  const normal = toUnitVector(orbitalNormal, "Orbital normal");
  const localY = normal.addScaledVector(localZ, -normal.dot(localZ));

  if (localY.lengthSq() <= BASIS_EPSILON) {
    throw new RangeError(
      "Orbital normal must not be parallel to the Moon-to-Earth direction",
    );
  }

  localY.normalize();
  const localX = new ThreeVector3().crossVectors(localY, localZ).normalize();
  const basis = new Matrix4().makeBasis(localX, localY, localZ);

  return new Quaternion().setFromRotationMatrix(basis).normalize();
}

export function phaseMarkerOffset(phasePosition: Vector3): Vector3 {
  return scale(normalize(phasePosition), MOON_RADIUS + PHASE_MARKER_GAP);
}

export function worldRadiusForPixelDiameter(
  distance: number,
  verticalFieldOfViewDegrees: number,
  viewportHeightPixels: number,
  diameterPixels: number,
): number {
  if (
    !Number.isFinite(distance) ||
    !Number.isFinite(verticalFieldOfViewDegrees) ||
    !Number.isFinite(viewportHeightPixels) ||
    !Number.isFinite(diameterPixels) ||
    distance <= 0 ||
    verticalFieldOfViewDegrees <= 0 ||
    verticalFieldOfViewDegrees >= 180 ||
    viewportHeightPixels <= 0 ||
    diameterPixels <= 0
  ) {
    throw new RangeError("Projection values must be finite and positive");
  }

  const halfFieldOfViewRadians = (verticalFieldOfViewDegrees * Math.PI) / 360;
  return (
    (distance * Math.tan(halfFieldOfViewRadians) * diameterPixels) /
    viewportHeightPixels
  );
}
